import { v4 as uuidv4 } from "uuid";

import isEquipeChatMobileApp from "./isEquipeChatMobileApp";

const MOBILE_PUSH_DEVICE_ID_KEY = "equipechatMobilePushDeviceId";
const MOBILE_PUSH_PENDING_TICKET_KEY = "equipechatMobilePendingTicketUuid";
const MOBILE_PUSH_CHANNEL_ID = "equipechat_messages_v2";
const MOBILE_PUSH_CHANNEL_NAME = "Mensagens do EquipeChat";
const MOBILE_PUSH_CHANNEL_DESCRIPTION =
  "Alertas de novas mensagens recebidas nos atendimentos.";
const MOBILE_PUSH_APP_VARIANT = "EquipeChat Teste";

const getCapacitorRuntime = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.Capacitor || null;
};

export const isNativeMobilePushRuntime = () =>
  isEquipeChatMobileApp() && typeof getCapacitorRuntime()?.isNativePlatform === "function"
    ? getCapacitorRuntime().isNativePlatform()
    : false;

export const getCapacitorPlugin = pluginName => {
  const runtime = getCapacitorRuntime();

  if (!runtime?.Plugins || !pluginName) {
    return null;
  }

  return runtime.Plugins[pluginName] || null;
};

export const getMobilePushChannelConfig = () => ({
  id: MOBILE_PUSH_CHANNEL_ID,
  name: MOBILE_PUSH_CHANNEL_NAME,
  description: MOBILE_PUSH_CHANNEL_DESCRIPTION,
  importance: 5,
  visibility: 1,
  sound: "default",
  lights: true,
  vibration: true,
});

export const getMobilePushChannelId = () => MOBILE_PUSH_CHANNEL_ID;

export const getMobilePushAppVariant = () => MOBILE_PUSH_APP_VARIANT;

export const getMobilePushPlatform = () => {
  const runtime = getCapacitorRuntime();

  if (typeof runtime?.getPlatform === "function") {
    return runtime.getPlatform();
  }

  return "web";
};

export const getOrCreateMobilePushDeviceId = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const existingId = String(
    window.localStorage.getItem(MOBILE_PUSH_DEVICE_ID_KEY) || ""
  ).trim();

  if (existingId) {
    return existingId;
  }

  const newId = uuidv4();
  window.localStorage.setItem(MOBILE_PUSH_DEVICE_ID_KEY, newId);
  return newId;
};

export const clearMobilePushDeviceId = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(MOBILE_PUSH_DEVICE_ID_KEY);
};

export const storePendingPushTicketUuid = ticketUuid => {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedTicketUuid = String(ticketUuid || "").trim();

  if (!normalizedTicketUuid) {
    return;
  }

  window.localStorage.setItem(
    MOBILE_PUSH_PENDING_TICKET_KEY,
    normalizedTicketUuid
  );
};

export const consumePendingPushTicketUuid = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const storedTicketUuid = String(
    window.localStorage.getItem(MOBILE_PUSH_PENDING_TICKET_KEY) || ""
  ).trim();

  if (storedTicketUuid) {
    window.localStorage.removeItem(MOBILE_PUSH_PENDING_TICKET_KEY);
  }

  return storedTicketUuid;
};

export const peekPendingPushTicketUuid = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return String(
    window.localStorage.getItem(MOBILE_PUSH_PENDING_TICKET_KEY) || ""
  ).trim();
};

const normalizeNotificationData = input => {
  if (!input || typeof input !== "object") {
    return {};
  }

  return input;
};

const toPositiveInteger = value => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return null;
  }

  return Math.round(parsedValue);
};

export const getNotificationTicketUuid = data => {
  const normalizedData = normalizeNotificationData(data);

  return String(
    normalizedData.ticketUuid ||
      normalizedData.ticketUUID ||
      normalizedData.ticketuuid ||
      ""
  ).trim();
};

export const getNotificationMessageId = data => {
  const normalizedData = normalizeNotificationData(data);

  return (
    toPositiveInteger(normalizedData.messageId) ||
    toPositiveInteger(normalizedData.messageID) ||
    toPositiveInteger(normalizedData.messageid)
  );
};

export const normalizeMobilePushPayload = notification => {
  const normalizedNotification = notification || {};
  const data = normalizeNotificationData(
    normalizedNotification?.notification?.data ||
      normalizedNotification?.data ||
      normalizedNotification?.extra ||
      {}
  );

  return {
    ticketUuid: getNotificationTicketUuid(data),
    messageId: getNotificationMessageId(data),
    title: String(
      normalizedNotification?.title ||
        normalizedNotification?.notification?.title ||
        data.title ||
        "AgilZap"
    ).trim(),
    body: String(
      normalizedNotification?.body ||
        normalizedNotification?.notification?.body ||
        data.body ||
        "Voce recebeu uma nova mensagem."
    ).trim(),
    data,
  };
};

export const unregisterCurrentMobilePushDevice = async api => {
  if (!api || !isNativeMobilePushRuntime()) {
    return false;
  }

  const deviceId = getOrCreateMobilePushDeviceId();

  if (!deviceId) {
    return false;
  }

  await api.delete("/mobile/push/devices/current", {
    data: { deviceId },
  });

  return true;
};
